/** AutoCAD's ribbon icons, redrawn.
 *
 * ⚠⚠ **THE ICONS ARE HALF OF WHY THE MODE READS AS AUTOCAD.** A drafter does not read the Modify
 * panel — they reach for the two-blue-lines-and-a-scissors of TRIM, the dashed ghost of COPY, the
 * grid of ARRAY. A panel of correctly-ordered generic glyphs still feels like somebody else's
 * app, which is exactly what the studio's own rail was.
 *
 * ⚠ **AutoCAD's icon language is NOT SketchUp's, and mixing them would be the tell.** SketchUp
 * draws saturated objects — a yellow pencil, a red arrow. AutoCAD draws a THIN LIGHT LINE DRAWING
 * of the geometry, with one or two colour accents marking what the command touches: the object is
 * pale grey, what is being added is blue, what is being removed is red, what is being measured is
 * green, the "before" state is a dashed ghost. Every icon below obeys that grammar, which is why
 * they read as a set even where the silhouettes are invented.
 *
 * ⚠ Redrawn, not copied — Autodesk's own art is Autodesk's. These are the same silhouettes and
 * the same grammar at 24 px, which is what memory actually keys on.
 *
 * ⚠ One `<Icon id>` switch rather than 200 exported components: the ribbon is DATA
 * (`catalog.ts`) and data holds ids, not React elements. A registry of components would need a
 * second table mapping id → component, which is one more place for a command to go missing.
 */
import * as React from "react";

/** AutoCAD's palette, named so the intent survives a colour tweak.
 *
 * ⚠⚠ **CSS VARIABLES, BECAUSE THE LIGHT THEME WAS HALF-BUILT.** These were flat hex, chosen
 * against the dark ribbon and correct there — and Options ▸ Display ▸ Color theme ▸ Light
 * repainted the chrome around them and left every icon where it was. Measured on the light
 * ribbon: the geometry line came out at **1.31:1**, where a glyph needs 3.0, and six of the
 * seven colours failed. Half the status bar and the Quick Access Toolbar were invisible.
 *
 * ⚠ A variable rather than a `theme` prop on `<Icon>`: there are ~200 icons reached from the
 * ribbon, the menus, the flyouts, the toolbars, the status bar and the command line, and a prop
 * would have to be threaded through all six. A variable cannot miss one. The fallback in each
 * `var()` is the DARK value, so an icon rendered outside the shell still looks right.
 *
 * ⚠ The light values are not the dark ones darkened — they are re-chosen to clear 3.0 against
 * all four surfaces an icon sits on (ribbon, panel, hover, sunk). `shell.tsx` defines them;
 * the measurement lives in that commit message. */
const LN = "var(--acad-ln, #CED5DA)";        // geometry — the line everything is drawn in
const LN_D = "var(--acad-lnd, #8B959C)";     // a second, quieter line: the ghost, the "before"
const BLU = "var(--acad-blu, #5AA0DA)";      // what the command ADDS or acts with
const YEL = "var(--acad-yel, #E3B23C)";      // what is selected, or the command's own handle
const RED = "var(--acad-red, #D9584F)";      // what the command REMOVES
const GRN = "var(--acad-grn, #5FAE63)";      // measurement and inspection
const MAG = "var(--acad-mag, #B57BD0)";      // constraints — the Parametric tab's own colour
const PAPER = "var(--acad-paper, #3C4147)";  // a filled face, seen against the ribbon
const INK = "var(--acad-ink, #20242A)";      // a recess: a screen, or a hole cut in PAPER

// ⚠ `fill` is deliberately NOT in here. A spread after an explicit `fill=` on the same
// element silently overrides it, and the icon then renders in the colour you did not
// choose — tsc flags it (TS2783) but only where both are literally on one tag. The default
// lives on the <svg> instead, where SVG's own inheritance carries it to every child that
// does not set its own.
const s = { strokeLinejoin: "round" as const, strokeLinecap: "round" as const };
const W = 1.5;

/** The dashed "before" outline that half of Modify's icons are built on. */
const Ghost = ({ d }: { d: string }) => (
  <path d={d} stroke={LN_D} strokeWidth={1.2} strokeDasharray="2.2 2" {...s} />
);

/** A sheet of paper, behind the file commands. */
const Sheet = ({ fold = true }: { fold?: boolean }) => (
  <g>
    <path d="M5.5 2.5h8.5l4.5 4.5v14.5h-13z" fill={PAPER} stroke={LN} strokeWidth={W} {...s} />
    {fold ? <path d="M14 2.5V7h4.5" stroke={LN} strokeWidth={W} {...s} /> : null}
  </g>
);

/** A magnifier, shared by the five zoom icons. */
const Glass = () => (
  <g>
    <circle cx="10.5" cy="10.5" r="6" fill="none" stroke={BLU} strokeWidth={1.8} />
    <path d="M15 15l5.5 5.5" stroke={LN} strokeWidth={2.2} {...s} />
  </g>
);

/** A stack of layers, for the Layers panel. */
const Stack = ({ hi = 1 }: { hi?: number }) => (
  <g>
    {[0, 1, 2].map((i) => (
      <path key={i} d={`M4 ${7.5 + i * 5}l8-3.4 8 3.4-8 3.4z`}
        fill={i === hi ? BLU : PAPER} stroke={i === hi ? BLU : LN} strokeWidth={1.2}
        fillOpacity={i === hi ? 0.5 : 1} {...s} />
    ))}
  </g>
);

/** AutoCAD's own snap markers — the shapes that appear at the cursor. */
const SNAP: Record<string, React.ReactNode> = {
  endpoint: <rect x="6" y="6" width="12" height="12" stroke={GRN} strokeWidth={1.8} {...s} />,
  midpoint: <path d="M12 5.5l6.5 12h-13z" stroke={GRN} strokeWidth={1.8} {...s} />,
  center: <circle cx="12" cy="12" r="6" stroke={GRN} strokeWidth={1.8} fill="none" />,
  geocenter: (<g stroke={GRN} strokeWidth={1.6} {...s}>
    <circle cx="12" cy="12" r="6" /><path d="M12 6v12M6 12h12" /></g>),
  node: (<g stroke={GRN} strokeWidth={1.7} {...s}>
    <circle cx="12" cy="12" r="6" /><path d="M7.8 7.8l8.4 8.4M16.2 7.8l-8.4 8.4" /></g>),
  quadrant: <path d="M12 5l7 7-7 7-7-7z" stroke={GRN} strokeWidth={1.7} {...s} />,
  intersection: <path d="M6 6l12 12M18 6L6 18" stroke={GRN} strokeWidth={2} {...s} />,
  extension: (<g stroke={GRN} strokeWidth={1.8} {...s}>
    <path d="M4 18h5" /><path d="M12 14h1.5M16 10h1.5M20 6h.5" strokeDasharray="0.1 3.4" /></g>),
  insertion: (<g stroke={GRN} strokeWidth={1.6} {...s}>
    <rect x="5" y="5" width="8" height="8" /><rect x="11" y="11" width="8" height="8" /></g>),
  perpendicular: <path d="M5 5v13h13M5 12h7v6" stroke={GRN} strokeWidth={1.8} {...s} />,
  tangent: (<g stroke={GRN} strokeWidth={1.7} {...s}>
    <circle cx="12" cy="14" r="5.5" /><path d="M4 6.5h16" /></g>),
  nearest: <path d="M5 17l7-9 7 9M5 17h14" stroke={GRN} strokeWidth={1.7} {...s} />,
  apparent: (<g stroke={GRN} strokeWidth={1.8} {...s}>
    <path d="M6 6l12 12" /><path d="M18 6L6 18" strokeDasharray="2.4 2" /></g>),
  parallel: <path d="M4 15L14 5M10 19L20 9" stroke={GRN} strokeWidth={1.8} {...s} />,
};

export function Icon({ id, size = 16 }: { id: string; size?: number }) {
  const p = { width: size, height: size, viewBox: "0 0 24 24", fill: "none" };
  const g = (children: React.ReactNode) => <svg {...p} aria-hidden>{children}</svg>;

  // ── object snaps ───────────────────────────────────────────────────────────────────────
  if (id.startsWith("osnap.")) {
    const k = id.slice(6);
    if (SNAP[k]) return g(SNAP[k]);
    return g(<g stroke={LN} strokeWidth={1.5} {...s}>
      <rect x="5" y="5" width="14" height="14" rx="1.5" /></g>);
  }

  switch (id) {
    // ── file, the QAT ───────────────────────────────────────────────────────────────────
    case "file.new":
      return g(<><Sheet /><path d="M12 11v7M8.5 14.5h7" stroke={BLU} strokeWidth={1.8} {...s} /></>);
    case "file.open": case "file.open.sheetset": case "file.open.dgn":
      return g(<g {...s}>
        <path d="M3 6.5h6.5l2 2.5H21v11H3z" fill={PAPER} stroke={LN} strokeWidth={W} />
        <path d="M3 20.5l3.5-8h18l-3.5 8z" fill="none" stroke={YEL} strokeWidth={W} /></g>);
    case "file.save": case "qat.save":
      return g(<g {...s}>
        <path d="M3.5 3.5h14l3 3v14h-17z" fill={PAPER} stroke={LN} strokeWidth={W} />
        <path d="M7 3.5h9v6H7z" fill={BLU} fillOpacity={0.55} stroke={BLU} strokeWidth={1.2} />
        <path d="M7 13.5h10v7H7z" stroke={LN} strokeWidth={1.2} /></g>);
    case "file.saveas": case "file.saveas.template": case "file.saveas.other":
      return g(<g {...s}>
        <path d="M3.5 3.5h12l3 3v9h-15z" fill={PAPER} stroke={LN} strokeWidth={W} />
        <path d="M13 21l7-7-2.5-2.5-7 7-.7 3.2z" fill={YEL} fillOpacity={0.4}
          stroke={YEL} strokeWidth={1.3} /></g>);
    case "cloud.open": case "cloud.save": case "col.share":
      return g(<g {...s}>
        <path d="M7 17.5a3.6 3.6 0 01.4-7.2 5 5 0 019.5-1.4 3.9 3.9 0 01.6 7.7z"
          fill={PAPER} stroke={LN} strokeWidth={W} />
        <path d={id === "cloud.save" ? "M12 20.5v-7M9 17l3 3.5 3-3.5" : "M12 13.5v7M9 17l3-3.5 3 3.5"}
          stroke={BLU} strokeWidth={1.7} {...s} /></g>);
    case "file.close": case "file.closeall": case "file.exit": case "win.close":
      return g(<g {...s}>
        <path d="M3.5 4.5h11v15h-11z" fill={PAPER} stroke={LN} strokeWidth={W} />
        <path d="M14 12h7M18 9l3 3-3 3" stroke={RED} strokeWidth={1.8} /></g>);
    case "out.plot": case "out.preview": case "out.batchplot":
      return g(<g {...s}>
        <path d="M7 3.5h10v5H7z" stroke={LN} strokeWidth={1.3} />
        <path d="M3.5 8.5h17v7h-17z" fill={PAPER} stroke={LN} strokeWidth={W} />
        <path d="M7 14h10v6.5H7z" fill={BLU} fillOpacity={0.28} stroke={BLU} strokeWidth={1.3} />
        <circle cx="17.5" cy="11.5" r="1" fill={GRN} stroke="none" /></g>);
    case "out.exportpdf": case "file.export.dxf": case "out.exportdwf":
    case "out.exportdgn": case "out.exportfbx": case "out.exportstl":
      return g(<><Sheet /><path d="M12.5 20.5h8M17 17l3.5 3.5-3.5 3.5"
        stroke={BLU} strokeWidth={1.7} {...s} /></>);
    case "insert.import": case "insert.pdfimport":
      return g(<><Sheet /><path d="M2 12.5h8M6.5 9l3.5 3.5-3.5 3.5"
        stroke={GRN} strokeWidth={1.7} {...s} /></>);
    case "edit.undo":
      return g(<path d="M7 8.5H15a5 5 0 010 10h-6M7 8.5l4-4M7 8.5l4 4"
        stroke={LN} strokeWidth={2} {...s} />);
    case "edit.redo":
      return g(<path d="M17 8.5H9a5 5 0 000 10h6M17 8.5l-4-4M17 8.5l-4 4"
        stroke={LN} strokeWidth={2} {...s} />);

    // ── Draw ────────────────────────────────────────────────────────────────────────────
    case "draw.line":
      return g(<g {...s}><path d="M4.5 19.5L19.5 4.5" stroke={LN} strokeWidth={W} />
        <circle cx="4.5" cy="19.5" r="2" fill={BLU} stroke="none" />
        <circle cx="19.5" cy="4.5" r="2" fill={BLU} stroke="none" /></g>);
    case "draw.pline": case "draw.3dpoly": case "mod.pedit":
      return g(<g {...s}><path d="M3.5 18L9 8l5 6 6.5-8.5" stroke={LN} strokeWidth={2.2} />
        <circle cx="3.5" cy="18" r="1.7" fill={BLU} stroke="none" />
        <circle cx="9" cy="8" r="1.7" fill={BLU} stroke="none" />
        <circle cx="14" cy="14" r="1.7" fill={BLU} stroke="none" />
        <circle cx="20.5" cy="5.5" r="1.7" fill={BLU} stroke="none" /></g>);
    case "draw.circle": case "draw.circle.cd": case "draw.circle.2p": case "draw.circle.3p":
    case "draw.circle.ttr": case "draw.circle.ttt": case "draw.circlegroup":
      return g(<g {...s}><circle cx="12" cy="12" r="8" stroke={LN} strokeWidth={W} />
        <path d="M12 12h8" stroke={BLU} strokeWidth={1.4} strokeDasharray="2.4 2" />
        <circle cx="12" cy="12" r="1.7" fill={BLU} stroke="none" /></g>);
    case "draw.arc": case "draw.arcgroup":
    case "draw.arc.3p": case "draw.arc.sce": case "draw.arc.sca": case "draw.arc.scl":
    case "draw.arc.sea": case "draw.arc.sed": case "draw.arc.ser": case "draw.arc.cse":
    case "draw.arc.csa": case "draw.arc.csl": case "draw.arc.cont":
      return g(<g {...s}><path d="M3.5 18.5A11 11 0 0120.5 18.5" stroke={LN} strokeWidth={W} />
        <circle cx="3.5" cy="18.5" r="1.8" fill={BLU} stroke="none" />
        <circle cx="20.5" cy="18.5" r="1.8" fill={BLU} stroke="none" />
        <circle cx="12" cy="7.2" r="1.5" fill={YEL} stroke="none" /></g>);
    case "draw.rect":
      return g(<g {...s}><rect x="3.5" y="6" width="17" height="12" stroke={LN} strokeWidth={W} />
        <circle cx="3.5" cy="6" r="1.8" fill={BLU} stroke="none" />
        <circle cx="20.5" cy="18" r="1.8" fill={BLU} stroke="none" /></g>);
    case "draw.polygon":
      return g(<path d="M12 3.5l8 5.8-3 9.4H7l-3-9.4z" stroke={LN} strokeWidth={W} {...s} />);
    case "draw.ellipse.axis": case "draw.ellipse.center": case "draw.ellipsegroup":
      return g(<ellipse cx="12" cy="12" rx="9" ry="5.5" stroke={LN} strokeWidth={W} fill="none" />);
    case "draw.ellipse.arc":
      return g(<path d="M3 12a9 5.5 0 0018 0" stroke={LN} strokeWidth={W} {...s} />);
    case "draw.hatch": case "pdvn.bucket":
      return g(<g {...s}>
        <rect x="3.5" y="5.5" width="17" height="13" stroke={LN} strokeWidth={1.3} />
        <path d="M5 15.5l6-8M9 18.5l8-11M14 18.5l6-8" stroke={BLU} strokeWidth={1.4} /></g>);
    case "draw.gradient":
      return g(<g {...s}>
        <defs><linearGradient id="acg" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor={BLU} /><stop offset="1" stopColor={PAPER} />
        </linearGradient></defs>
        <rect x="3.5" y="5.5" width="17" height="13" fill="url(#acg)"
          stroke={LN} strokeWidth={1.3} /></g>);
    case "draw.boundary": case "draw.region":
      return g(<g {...s}><path d="M4 18l3-11 6 4 4-6 3 13z" fill={PAPER}
        stroke={BLU} strokeWidth={W} /></g>);
    case "draw.xline": case "draw.ray":
      return g(<g {...s}>
        <path d="M2 20L22 4" stroke={BLU} strokeWidth={1.4} strokeDasharray="4 2.6" />
        <circle cx="12" cy="12" r="1.9" fill={YEL} stroke="none" /></g>);
    case "draw.mline":
      return g(<g {...s}><path d="M3 17.5L21 6M3 20.5L21 9" stroke={LN} strokeWidth={W} /></g>);
    case "draw.spline.fit": case "draw.spline.cv": case "draw.splinegroup":
    case "mod.splinedit":
      return g(<g {...s}>
        <path d="M3.5 17C7 6 10 20.5 13.5 10.5S19 4.5 20.5 8" stroke={LN} strokeWidth={W} />
        <circle cx="3.5" cy="17" r="1.5" fill={BLU} stroke="none" />
        <circle cx="13.5" cy="10.5" r="1.5" fill={BLU} stroke="none" />
        <circle cx="20.5" cy="8" r="1.5" fill={BLU} stroke="none" /></g>);
    case "draw.revcloud": case "draw.revcloud.rect": case "draw.revcloud.free":
      return g(<path d="M5 14a2.6 2.6 0 013.2-3.4A3 3 0 0113 8.4a2.7 2.7 0 014.6 1.5
        2.7 2.7 0 01.6 5.3 2.8 2.8 0 01-4.6 1.7 3 3 0 01-4.8-.3A2.6 2.6 0 015 14z"
        stroke={BLU} strokeWidth={W} {...s} />);
    case "draw.donut":
      return g(<g {...s}><circle cx="12" cy="12" r="8.2" stroke={LN} strokeWidth={W} />
        <circle cx="12" cy="12" r="4" stroke={LN} strokeWidth={W} /></g>);
    case "draw.wipeout":
      return g(<g {...s}><rect x="4" y="6" width="16" height="12" fill={PAPER}
        stroke={LN} strokeWidth={W} />
        <path d="M4 18L20 6" stroke={RED} strokeWidth={1.4} /></g>);
    case "draw.helix":
      return g(<path d="M7 5.5c0 2 10 2 10 4s-10 2-10 4 10 2 10 4-10 2-10 2"
        stroke={LN} strokeWidth={W} {...s} />);
    case "draw.point": case "draw.pointstyle": case "util.id": case "draw.measurepts":
      return g(<g {...s}><path d="M12 4v16M4 12h16" stroke={LN} strokeWidth={1.2} />
        <circle cx="12" cy="12" r="2.6" fill={BLU} stroke="none" /></g>);

    // ── the clipboard, which is a whole panel of grey and still has to read as itself ───
    case "clip.paste": case "clip.pasteblock": case "clip.pasteorig": case "clip.pastespec":
      return g(<g {...s}>
        <path d="M6 4.5h12v16H6z" fill={PAPER} stroke={LN} strokeWidth={W} />
        <path d="M9.5 2.5h5v3.5h-5z" fill={LN_D} stroke={LN} strokeWidth={1.2} />
        <path d="M11 20.5h9.5v-9H11z"
          fill={id === "clip.pasteblock" ? BLU : PAPER} fillOpacity={0.45}
          stroke={id === "clip.pasteorig" ? YEL : BLU} strokeWidth={1.4} />
        {id === "clip.pastespec"
          ? <path d="M14 14.5h3.5M14 17h3.5" stroke={LN} strokeWidth={1.2} /> : null}</g>);
    case "prop.list": case "anno.text.scale": case "insert.pdflayers":
      return g(<g {...s}>
        <path d="M4.5 3.5h15v17h-15z" fill={PAPER} stroke={LN} strokeWidth={1.3} />
        <path d="M7.5 8h9M7.5 11.5h9M7.5 15h5.5" stroke={BLU} strokeWidth={1.4} /></g>);
    case "insert.xref":
      return g(<g {...s}>
        <path d="M2.5 5h10v9h-10z" fill={PAPER} stroke={LN} strokeWidth={1.3} />
        <path d="M11.5 10h10v9h-10z" fill={PAPER} stroke={BLU} strokeWidth={1.4} /></g>);
    case "pdvn.rotrect":
      return g(<g {...s}>
        <path d="M3.5 13.5L9 5l11.5 5.5L15 19z" stroke={LN} strokeWidth={W} />
        <circle cx="3.5" cy="13.5" r="1.7" fill={BLU} stroke="none" />
        <circle cx="9" cy="5" r="1.7" fill={BLU} stroke="none" />
        <circle cx="15" cy="19" r="1.7" fill={YEL} stroke="none" /></g>);
    case "pdvn.sides":
      return g(<g {...s}>
        <path d="M12 3.5l7.4 4.3v8.4L12 20.5l-7.4-4.3V7.8z" stroke={LN} strokeWidth={W} />
        {[[12, 3.5], [19.4, 7.8], [19.4, 16.2], [12, 20.5], [4.6, 16.2], [4.6, 7.8]].map(
          ([x, y], i) => <circle key={i} cx={x} cy={y} r="1.4" fill={BLU} stroke="none" />)}</g>);

    // ── Modify ──────────────────────────────────────────────────────────────────────────
    case "mod.move":
      return g(<g {...s}><Ghost d="M3.5 12.5h8v8h-8z" />
        <rect x="10" y="4" width="10" height="10" stroke={BLU} strokeWidth={W} />
        <path d="M6.5 10.5l4-4M8 6.5h2.7v2.7" stroke={YEL} strokeWidth={1.5} /></g>);
    case "mod.copy": case "clip.copy": case "clip.copybase": case "exp.copym":
    case "exp.ncopy": case "layer.copyto":
      return g(<g {...s}><rect x="3.5" y="8.5" width="11" height="11"
        fill={PAPER} stroke={LN} strokeWidth={1.3} />
        <rect x="9.5" y="4" width="11" height="11" stroke={BLU} strokeWidth={W} /></g>);
    case "mod.rotate":
      return g(<g {...s}><Ghost d="M8 16h8v5H8z" />
        <path d="M12 3.2A8.8 8.8 0 1120.5 14" stroke={LN} strokeWidth={W} />
        <path d="M20.5 14l-3.2-1.4M20.5 14l1.6-3" stroke={YEL} strokeWidth={1.7} />
        <circle cx="12" cy="12" r="1.7" fill={BLU} stroke="none" /></g>);
    case "mod.scale":
      return g(<g {...s}><Ghost d="M5 13.5h6v6H5z" />
        <rect x="5" y="5" width="14" height="14" stroke={BLU} strokeWidth={W} />
        <path d="M12 12l6-6M14.5 6h3.7v3.7" stroke={YEL} strokeWidth={1.5} /></g>);
    case "mod.mirror":
      return g(<g {...s}><path d="M12 2.5v19" stroke={YEL} strokeWidth={1.4}
        strokeDasharray="3 2.2" />
        <path d="M9.5 5.5L3 12l6.5 6.5z" fill={PAPER} stroke={LN} strokeWidth={1.3} />
        <path d="M14.5 5.5L21 12l-6.5 6.5z" stroke={BLU} strokeWidth={W} /></g>);
    case "mod.offset":
      return g(<g {...s}><path d="M5 19.5V8l7-4.5" stroke={LN} strokeWidth={W} />
        <path d="M9 19.5v-9l6-4" stroke={BLU} strokeWidth={W} strokeDasharray="3 2" /></g>);
    case "mod.array.rect":
      return g(<g stroke={LN} strokeWidth={1.3} {...s}>
        {[0, 1, 2].map((r) => [0, 1, 2].map((c) => (
          <rect key={`${r}${c}`} x={3 + c * 6.4} y={3 + r * 6.4} width="5" height="5"
            stroke={r === 0 && c === 0 ? BLU : LN} strokeWidth={r === 0 && c === 0 ? 1.8 : 1.3} />
        )))}</g>);
    case "mod.array.polar":
      return g(<g {...s}><circle cx="12" cy="12" r="8" stroke={LN_D} strokeWidth={1.1}
        strokeDasharray="2.4 2" />
        {[0, 1, 2, 3, 4, 5].map((i) => {
          const a = (i / 6) * Math.PI * 2 - Math.PI / 2;
          return <rect key={i} x={12 + Math.cos(a) * 8 - 2} y={12 + Math.sin(a) * 8 - 2}
            width="4" height="4" stroke={i === 0 ? BLU : LN} strokeWidth={i === 0 ? 1.7 : 1.2} />;
        })}</g>);
    case "mod.array.path":
      return g(<g {...s}><path d="M2.5 18C7 18 8 7 12.5 7S19 15 21.5 15"
        stroke={LN_D} strokeWidth={1.2} strokeDasharray="2.4 2" />
        {[[3.5, 18], [9, 9.8], [16, 10.6], [21, 15]].map(([x, y], i) => (
          <rect key={i} x={x - 2} y={y - 2} width="4" height="4"
            stroke={i === 0 ? BLU : LN} strokeWidth={i === 0 ? 1.7 : 1.2} />))}</g>);
    case "mod.trim":
      return g(<g {...s}><path d="M3 15h18" stroke={LN} strokeWidth={W} />
        <path d="M15 15h6" stroke={RED} strokeWidth={2.2} strokeDasharray="2.5 2" />
        <path d="M12 3.5v17" stroke={YEL} strokeWidth={1.4} />
        <circle cx="17" cy="8" r="2.3" stroke={LN} strokeWidth={1.3} />
        <path d="M18.6 9.6l2.4 2.4" stroke={LN} strokeWidth={1.3} /></g>);
    case "mod.extend":
      return g(<g {...s}><path d="M3 15h9" stroke={LN} strokeWidth={W} />
        <path d="M12 15h8M17 12l3 3-3 3" stroke={BLU} strokeWidth={1.8} />
        <path d="M20.5 3.5v17" stroke={YEL} strokeWidth={1.4} /></g>);
    case "mod.fillet":
      return g(<g {...s}><Ghost d="M5 4.5v15h15" />
        <path d="M5 4.5v7a8 8 0 008 8h7" stroke={BLU} strokeWidth={W} /></g>);
    case "mod.chamfer":
      return g(<g {...s}><Ghost d="M5 4.5v15h15" />
        <path d="M5 4.5v6.5l8.5 8.5H20" stroke={BLU} strokeWidth={W} /></g>);
    case "mod.blend":
      return g(<g {...s}><path d="M3 6h5M16 18h5" stroke={LN} strokeWidth={W} />
        <path d="M8 6c5 0 3 12 8 12" stroke={BLU} strokeWidth={W} /></g>);
    case "mod.stretch":
      return g(<g {...s}><Ghost d="M5 8h9v9H5z" />
        <path d="M5 8h15v9H5z" stroke={BLU} strokeWidth={W} />
        <path d="M14 20.5h6M17 18l3 2.5-3 2.5" stroke={YEL} strokeWidth={1.5} /></g>);
    case "mod.erase": case "clip.cut":
      return g(<g {...s}>
        <path d="M9 20.5L3.5 15 14 4.5l5.5 5.5z" fill={PAPER} stroke={LN} strokeWidth={W} />
        <path d="M9 20.5h11.5" stroke={RED} strokeWidth={2} />
        <path d="M7.2 11.3l5.5 5.5" stroke={RED} strokeWidth={1.2} /></g>);
    case "mod.explode": case "exp.explode":
      return g(<g {...s}><path d="M12 12L5 5M12 12l7-7M12 12l-7 7M12 12l7 7"
        stroke={YEL} strokeWidth={1.3} strokeDasharray="2.6 2" />
        {[[5, 5], [19, 5], [5, 19], [19, 19]].map(([x, y], i) => (
          <rect key={i} x={x - 3} y={y - 3} width="6" height="6" stroke={LN}
            strokeWidth={1.4} />))}</g>);
    case "mod.break": case "mod.breakat": case "exp.breakline":
      return g(<g {...s}><path d="M3 12h6M15 12h6" stroke={LN} strokeWidth={2.2} />
        <path d="M10 6.5l2 5.5-2 5.5M14 6.5l-2 5.5 2 5.5" stroke={RED} strokeWidth={1.4} /></g>);
    case "mod.join":
      return g(<g {...s}><path d="M3 12h8.5M12.5 12H21" stroke={LN} strokeWidth={2.2} />
        <circle cx="12" cy="12" r="3.2" stroke={BLU} strokeWidth={1.7} /></g>);
    case "mod.align":
      return g(<g {...s}><path d="M4 3.5v17" stroke={YEL} strokeWidth={1.5} />
        <Ghost d="M9 14h9v6H9z" />
        <path d="M4 4.5h13v6H4z" stroke={BLU} strokeWidth={W} /></g>);
    case "mod.reverse":
      return g(<g {...s}><path d="M3 9h18M18 6l3 3-3 3" stroke={LN} strokeWidth={1.6} />
        <path d="M21 16H3M6 13l-3 3 3 3" stroke={BLU} strokeWidth={1.6} /></g>);
    case "mod.lengthen":
      return g(<g {...s}><path d="M4 12h10" stroke={LN} strokeWidth={2.2} />
        <path d="M14 12h6M17.5 9l3 3-3 3" stroke={BLU} strokeWidth={1.7} />
        <path d="M4 8v8" stroke={LN} strokeWidth={1.3} /></g>);
    case "mod.divide": case "pdvn.divide":
      return g(<g {...s}><path d="M3 15h18" stroke={LN} strokeWidth={2.2} />
        {[8, 13, 18].map((x) => (
          <path key={x} d={`M${x} 10.5v9`} stroke={BLU} strokeWidth={1.6} />))}</g>);
    case "mod.overkill": case "exp.overkill":
      return g(<g {...s}><path d="M4 8h13M4 13h13" stroke={LN} strokeWidth={1.8} />
        <path d="M4 18h13" stroke={RED} strokeWidth={1.8} />
        <path d="M17 15.5l4 5M21 15.5l-4 5" stroke={RED} strokeWidth={1.6} /></g>);
    case "mod.setbylayer": case "mod.chspace": case "mod.changetospace":
    case "layer.tocurrent":
      return g(<><Stack /><path d="M15.5 19.5h6M18.5 16.5l3 3-3 3"
        stroke={BLU} strokeWidth={1.6} {...s} /></>);
    case "mod.hatchedit": case "mod.textedit": case "mod.object": case "block.edit":
    case "mod.clip": case "insert.clip": case "mod.clip.viewport": case "insert.adjust":
      return g(<g {...s}><rect x="3.5" y="5" width="12" height="12" fill={PAPER}
        stroke={LN} strokeWidth={1.3} />
        <path d="M12 21l8.5-8.5-3-3L9 18l-.6 3.6z" fill={YEL} fillOpacity={0.35}
          stroke={YEL} strokeWidth={1.3} /></g>);

    // ── booleans ────────────────────────────────────────────────────────────────────────
    case "pdvn.union": case "pdvn.subtract": case "pdvn.intersect": case "mod.solidedit": {
      const which = id.split(".")[1];
      return g(<g {...s}>
        <circle cx="9.5" cy="12" r="6.4" fill={which === "intersect" ? "none" : BLU}
          fillOpacity={which === "subtract" ? 0.45 : 0.45} stroke={LN} strokeWidth={1.3} />
        <circle cx="14.5" cy="12" r="6.4"
          fill={which === "union" ? BLU : "none"} fillOpacity={0.45}
          stroke={which === "subtract" ? RED : LN} strokeWidth={1.3} />
        {which === "intersect"
          ? <path d="M12 6.6a6.4 6.4 0 000 10.8 6.4 6.4 0 000-10.8z" fill={BLU}
              fillOpacity={0.6} stroke="none" /> : null}
        {which === "subtract"
          ? <path d="M12 6.6a6.4 6.4 0 000 10.8 6.4 6.4 0 000-10.8z" fill={INK}
              stroke="none" /> : null}
      </g>);
    }

    // ── Annotation ──────────────────────────────────────────────────────────────────────
    case "anno.text.single": case "anno.text.group":
      return g(<g {...s}><path d="M4 6.5h16M12 6.5v13M9 19.5h6" stroke={LN} strokeWidth={2} />
        <path d="M20.5 4v6" stroke={BLU} strokeWidth={1.4} /></g>);
    case "anno.text.multi": case "anno.table": case "exp.txt2mtxt":
      return g(<g {...s}><rect x="3.5" y="4.5" width="17" height="15" stroke={LN}
        strokeWidth={1.3} />
        <path d="M6.5 9h11M6.5 12.5h11M6.5 16h7" stroke={BLU} strokeWidth={1.5} /></g>);
    case "anno.style.text": case "anno.style.dim": case "anno.style.table":
    case "anno.style.mleader": case "pal.styles": case "exp.txtexp": case "exp.arctext":
      return g(<g {...s}><path d="M4 8h10M9 8v11M6.5 19h5" stroke={LN} strokeWidth={1.8} />
        <circle cx="17.5" cy="15.5" r="3.4" stroke={YEL} strokeWidth={1.5} />
        <path d="M17.5 9.5v2M17.5 19.5v2M13 15.5h2M20 15.5h2" stroke={YEL}
          strokeWidth={1.4} /></g>);
    case "anno.dim.linear": case "anno.dim.continue": case "anno.dim.baseline":
    case "anno.dim.quick": case "anno.dim.ordinate": case "util.measure.dist":
      return g(<g {...s}><path d="M4 6v12M20 6v12" stroke={LN} strokeWidth={1.4} />
        <path d="M4 12h16M6.5 9.5L4 12l2.5 2.5M17.5 9.5L20 12l-2.5 2.5"
          stroke={GRN} strokeWidth={1.6} /></g>);
    case "anno.dim.aligned":
      return g(<g {...s}><path d="M4 9l4-4M16 21l4-4" stroke={LN} strokeWidth={1.4} />
        <path d="M6 7l12 12M6.6 10.4L6 7l3.4.6M17.4 15.6L18 19l-3.4-.6"
          stroke={GRN} strokeWidth={1.6} /></g>);
    case "anno.dim.angular": case "par.dim.angular": case "util.measure.angle":
      return g(<g {...s}><path d="M4 20L20 4M4 20h17" stroke={LN} strokeWidth={1.4} />
        <path d="M15 20a11 11 0 00-3.2-7.8" stroke={GRN} strokeWidth={1.7} /></g>);
    case "anno.dim.radius": case "anno.dim.diameter": case "anno.dim.jogged":
    case "par.dim.radial": case "util.measure.radius":
      return g(<g {...s}><circle cx="12" cy="12" r="8" stroke={LN} strokeWidth={1.3} />
        <path d="M12 12l5.7 5.7M14.4 12.6L12 12l.6 2.4" stroke={GRN} strokeWidth={1.6} /></g>);
    case "anno.dim.arc":
      return g(<g {...s}><path d="M4 18a10 10 0 0116 0" stroke={LN} strokeWidth={1.4} />
        <path d="M6 13.6a7.5 7.5 0 0112 0" stroke={GRN} strokeWidth={1.7} /></g>);
    case "anno.dim.break": case "anno.dim.space": case "anno.dim.jogline":
      return g(<g {...s}><path d="M4 8h16M4 16h16" stroke={LN} strokeWidth={1.4} />
        <path d="M10 6l2 2-2 2M14 14l-2 2 2 2" stroke={GRN} strokeWidth={1.5} /></g>);
    case "anno.leader": case "anno.leader.add": case "anno.leader.remove":
    case "anno.leader.align": case "anno.leader.collect": case "pdvn.arrow":
      return g(<g {...s}><path d="M3.5 19.5l7-6h10" stroke={LN} strokeWidth={1.7} />
        <path d="M3.5 19.5l4.2-1.1-1.5-2.4z" fill={BLU} stroke={BLU} strokeWidth={1} />
        <path d="M11.5 9.5h9" stroke={BLU} strokeWidth={1.8} /></g>);
    case "anno.centermark": case "anno.centerline":
      return g(<g {...s}><circle cx="12" cy="12" r="7" stroke={LN} strokeWidth={1.3} />
        <path d="M12 3.5v17M3.5 12h17" stroke={YEL} strokeWidth={1.2}
          strokeDasharray="5 2 1.5 2" /></g>);
    case "anno.text.find": case "anno.text.spell": case "util.quickselect":
      return g(<><Glass /><path d="M7.5 9h6M7.5 12h4" stroke={LN} strokeWidth={1.3} {...s} /></>);
    case "anno.addscale": case "anno.syncscale": case "anno.visibility":
    case "pdvn.setscale": case "st.annoscale":
      return g(<g {...s}><path d="M3 17.5L20.5 5" stroke={LN} strokeWidth={1.6} />
        <path d="M6 14.5l2 2M10 11.5l2 2M14 8.5l2 2" stroke={GRN} strokeWidth={1.4} /></g>);

    // ── Layers ──────────────────────────────────────────────────────────────────────────
    case "layer.props": case "layer.states": case "layer.walk": case "layer.merge":
      return g(<Stack />);
    case "layer.off": case "layer.on": case "st.isolate": case "layer.iso":
    case "layer.unisolate":
      return g(<g {...s}><path d="M2.5 12s3.6-6 9.5-6 9.5 6 9.5 6-3.6 6-9.5 6-9.5-6-9.5-6z"
        stroke={LN} strokeWidth={1.4} />
        <circle cx="12" cy="12" r="2.8" fill={BLU} stroke="none" />
        {id === "layer.off" || id === "layer.iso"
          ? <path d="M4 20L20 4" stroke={RED} strokeWidth={1.8} /> : null}</g>);
    case "layer.freeze":
      return g(<g stroke={BLU} strokeWidth={1.5} {...s}>
        <path d="M12 3v18M4.2 7.5l15.6 9M19.8 7.5l-15.6 9" /></g>);
    case "layer.lock": case "layer.unlock": case "ui.lockui": case "st.lockui":
    case "ui.lock.toolbars": case "ui.lock.windows": case "ui.lock.all":
      return g(<g {...s}><rect x="5" y="11" width="14" height="9.5" fill={PAPER}
        stroke={LN} strokeWidth={1.4} />
        <path d={id === "layer.unlock" ? "M8.5 11V8a3.5 3.5 0 017 0" : "M8.5 11V8a3.5 3.5 0 017 0v3"}
          stroke={YEL} strokeWidth={1.6} /></g>);
    case "layer.makecurrent": case "layer.match": case "prop.match": case "layer.previous":
      return g(<><Stack /><path d="M16.5 4.5l4 4M20.5 4.5l-4 4"
        stroke={id === "layer.previous" ? BLU : YEL} strokeWidth={1.6} {...s} /></>);
    case "layer.delete":
      return g(<><Stack /><path d="M15.5 4l5 5M20.5 4l-5 5" stroke={RED}
        strokeWidth={1.8} {...s} /></>);
    case "layer.control": case "prop.colour": case "prop.linetype": case "prop.lineweight":
    case "prop.transparency":
      return g(<g {...s}><rect x="3.5" y="7" width="17" height="10" stroke={LN}
        strokeWidth={1.3} /><path d="M14 11l2.5 2.5L19 11" stroke={LN} strokeWidth={1.5} /></g>);

    // ── Blocks ──────────────────────────────────────────────────────────────────────────
    case "block.insert": case "pal.blocks": case "pal.toolpalettes":
      return g(<g {...s}><path d="M12 2.5l8.5 4.8v9.4L12 21.5 3.5 16.7V7.3z"
        fill={PAPER} stroke={BLU} strokeWidth={W} />
        <path d="M3.5 7.3L12 12.1l8.5-4.8M12 12.1v9.4" stroke={LN} strokeWidth={1.2} /></g>);
    case "block.create": case "block.wblock": case "group.create":
      return g(<g {...s}><rect x="7" y="7" width="12" height="12" fill={PAPER}
        stroke={BLU} strokeWidth={W} />
        {[[4, 4], [4, 19], [19, 4], [19, 19]].map(([x, y], i) => (
          <rect key={i} x={x - 1.6} y={y - 1.6} width="3.2" height="3.2"
            fill={YEL} stroke="none" />))}</g>);
    case "block.editor": case "block.attdef": case "block.attsync": case "block.setbase":
    case "block.attmanage": case "block.recent": case "block.favourites":
      return g(<g {...s}><path d="M12 3.5l7.5 4.3v8.4L12 20.5 4.5 16.2V7.8z"
        fill="none" stroke={LN} strokeWidth={1.3} />
        <path d="M9 12h6M12 9v6" stroke={YEL} strokeWidth={1.6} /></g>);
    // ⚠ The same block outline as BEDIT above, with a tick and a cross — the pair reads as
    // "finish this edit" only if it keeps the shape of the thing being finished.
    case "block.savedef":
      return g(<g {...s}><path d="M12 3.5l7.5 4.3v8.4L12 20.5 4.5 16.2V7.8z"
        fill="none" stroke={LN} strokeWidth={1.3} />
        <path d="M8.4 12.2l2.6 2.6 4.8-5.2" stroke={GRN} strokeWidth={1.9} fill="none" /></g>);
    case "block.discard":
      return g(<g {...s}><path d="M12 3.5l7.5 4.3v8.4L12 20.5 4.5 16.2V7.8z"
        fill="none" stroke={LN} strokeWidth={1.3} />
        <path d="M9.3 9.3l5.4 5.4M14.7 9.3l-5.4 5.4" stroke={RED} strokeWidth={1.9} /></g>);

    // ── palettes and views ──────────────────────────────────────────────────────────────
    case "prop.palette": case "pal.designcenter": case "pal.sheetset": case "pal.markup":
    case "pal.xref": case "pal.items": case "pal.details": case "pal.rooms":
      return g(<g {...s}><rect x="3.5" y="3.5" width="17" height="17" fill={PAPER}
        stroke={LN} strokeWidth={1.3} />
        <path d="M3.5 8h17" stroke={LN} strokeWidth={1.3} />
        <path d="M6.5 11.5h5M6.5 15h5M14 11.5h3.5M14 15h3.5" stroke={BLU}
          strokeWidth={1.4} /></g>);
    case "pal.history": case "man.record": case "man.play":
      return g(<g {...s}><circle cx="12" cy="12" r="8.4" stroke={LN} strokeWidth={1.4} />
        <path d="M12 6.8V12l4 2.6" stroke={BLU} strokeWidth={1.7} /></g>);
    case "pal.stairs": case "pdvn.stair":
      return g(<g {...s}><path d="M3.5 20.5V16h5v-4h5V7.5h5V3.5"
        stroke={LN} strokeWidth={1.7} />
        <path d="M3.5 20.5h5v-4.5h5v-4.5h5V7h2.5" stroke={BLU} strokeWidth={1.2} /></g>);
    case "pal.joinery": case "pdvn.buildup":
      return g(<g {...s}><rect x="3.5" y="6" width="17" height="4" fill={PAPER}
        stroke={LN} strokeWidth={1.2} />
        <rect x="3.5" y="10" width="17" height="3" fill={BLU} fillOpacity={0.4}
          stroke={BLU} strokeWidth={1.2} />
        <rect x="3.5" y="13" width="17" height="5" stroke={LN} strokeWidth={1.2} /></g>);
    case "view.zoom.extents": case "view.zoom.all":
      return g(<><Glass /><path d="M8 8h5M8 8v5M13 13H8M13 13V8" stroke={YEL}
        strokeWidth={1.5} {...s} /></>);
    case "view.zoom.window":
      return g(<><Glass /><rect x="8" y="8" width="5" height="5" stroke={YEL}
        strokeWidth={1.5} fill="none" /></>);
    case "view.zoom.prev": case "view.regen": case "view.regenall":
      return g(<path d="M20 12a8 8 0 11-2.4-5.7M20 4v4h-4" stroke={LN}
        strokeWidth={1.7} {...s} />);
    case "view.zoom.realtime": case "view.zoomgroup":
      return g(<><Glass /><path d="M8 10.5h5M10.5 8v5" stroke={YEL}
        strokeWidth={1.5} {...s} /></>);
    case "view.pan": case "view.pan.point":
      return g(<path d="M12 3.5v17M3.5 12h17M12 3.5l-2.6 2.6M12 3.5l2.6 2.6M12 20.5l-2.6-2.6
        M12 20.5l2.6-2.6M3.5 12l2.6-2.6M3.5 12l2.6 2.6M20.5 12l-2.6-2.6M20.5 12l-2.6 2.6"
        stroke={LN} strokeWidth={1.5} {...s} />);
    case "view.orbit": case "view.orbit.free": case "view.orbit.cont": case "view.viewcube":
    case "view.3dviews": case "view.base": case "ws.3dmodel": case "ws.3dbasics":
      return g(<g {...s}><path d="M12 3.2l8 4.4v8.8l-8 4.4-8-4.4V7.6z" fill={PAPER}
        stroke={LN} strokeWidth={1.4} />
        <path d="M4 7.6l8 4.4 8-4.4M12 12v8.8" stroke={LN_D} strokeWidth={1.2} />
        <path d="M12 3.2l8 4.4-8 4.4-8-4.4z" fill={BLU} fillOpacity={0.45} stroke="none" /></g>);
    case "view.named": case "view.vports": case "view.vports.join": case "view.vports.restore":
    case "ui.filetabs": case "ui.layouttabs":
      return g(<g {...s}><rect x="3.5" y="4.5" width="17" height="15" stroke={LN}
        strokeWidth={1.4} /><path d="M12 4.5v15M3.5 12h8.5" stroke={BLU} strokeWidth={1.3} /></g>);
    case "view.ucsicon":
      return g(<g {...s}><path d="M5 5v14h14" stroke={LN} strokeWidth={1.8} />
        <path d="M5 5l-2.2 3.4M5 5l2.2 3.4M19 19l-3.4-2.2M19 19l-3.4 2.2"
          stroke={LN} strokeWidth={1.4} />
        <text x="8.4" y="9.4" fontSize="6.4" fill={GRN} stroke="none"
          fontFamily="ui-monospace, monospace">Y</text>
        <text x="15.4" y="16.6" fontSize="6.4" fill={RED} stroke="none"
          fontFamily="ui-monospace, monospace">X</text></g>);
    case "view.navbar":
      return g(<g {...s}><rect x="8.5" y="2.5" width="7" height="19" rx="3.4"
        stroke={LN} strokeWidth={1.4} />
        <circle cx="12" cy="7" r="1.6" fill={BLU} stroke="none" />
        <circle cx="12" cy="12" r="1.6" fill={LN} stroke="none" />
        <circle cx="12" cy="17" r="1.6" fill={LN} stroke="none" /></g>);
    case "ui.cleanscreen":
      return g(<g stroke={LN} strokeWidth={1.7} {...s}>
        <path d="M3.5 9V3.5H9M15 3.5h5.5V9M20.5 15v5.5H15M9 20.5H3.5V15" /></g>);
    case "ui.cmdline": case "man.runscript": case "man.vlisp": case "man.vba":
      return g(<g {...s}><rect x="2.5" y="5.5" width="19" height="13" fill={INK}
        stroke={LN} strokeWidth={1.3} />
        <path d="M6 10l3 2.5L6 15" stroke={GRN} strokeWidth={1.6} />
        <path d="M11.5 15.5h6" stroke={GRN} strokeWidth={1.6} /></g>);
    case "ui.menubar": case "ui.toolbars": case "man.cui": case "man.tp":
      return g(<g {...s}><rect x="2.5" y="4.5" width="19" height="15" stroke={LN}
        strokeWidth={1.3} /><rect x="2.5" y="4.5" width="19" height="4" fill={BLU}
        fillOpacity={0.4} stroke={LN} strokeWidth={1.3} /></g>);

    // ── utilities, references, data ─────────────────────────────────────────────────────
    case "util.measure.area": case "util.measure.volume": case "util.measure.quick":
      return g(<g {...s}><path d="M4 18.5l3-11 6 3.5 4-6 3 13.5z" fill={GRN}
        fillOpacity={0.25} stroke={GRN} strokeWidth={1.5} /></g>);
    case "util.calc":
      return g(<g {...s}><rect x="5" y="2.5" width="14" height="19" rx="1.5" fill={PAPER}
        stroke={LN} strokeWidth={1.4} />
        <rect x="7.5" y="5" width="9" height="3.5" fill={BLU} fillOpacity={0.4}
          stroke={BLU} strokeWidth={1} />
        {[0, 1, 2].map((r) => [0, 1, 2].map((c) => (
          <circle key={`${r}${c}`} cx={8.5 + c * 3.5} cy={12 + r * 3} r="1"
            fill={LN} stroke="none" />)))}</g>);
    case "util.purge": case "exp.flatten":
      return g(<g {...s}><path d="M4 6.5h16M9.5 6.5V4h5v2.5" stroke={LN} strokeWidth={1.4} />
        <path d="M6.5 6.5l1 14h9l1-14z" fill={PAPER} stroke={RED} strokeWidth={1.5} /></g>);
    case "util.selectsimilar": case "util.addselected": case "group.edit":
    case "group.selection": case "group.ungroup":
      return g(<g {...s}><rect x="3" y="3.5" width="8" height="8" stroke={BLU}
        strokeWidth={1.5} /><rect x="13" y="3.5" width="8" height="8" stroke={LN}
        strokeWidth={1.3} /><rect x="3" y="13.5" width="8" height="8" stroke={LN}
        strokeWidth={1.3} /><rect x="13" y="13.5" width="8" height="8"
        stroke={id === "group.ungroup" ? RED : LN} strokeWidth={1.3} /></g>);
    case "insert.attach": case "insert.dwgref": case "insert.uframe":
      return g(<g {...s}><path d="M13 6l-6.4 6.4a3.4 3.4 0 004.8 4.8L18 10.6a5.2 5.2 0
        00-7.4-7.4L4.8 9" stroke={BLU} strokeWidth={1.6} /></g>);
    case "insert.underlay.fade": case "st.transparency":
      return g(<g {...s}><rect x="3.5" y="5.5" width="17" height="13" stroke={LN}
        strokeWidth={1.3} />
        {[0, 1, 2, 3].map((i) => (
          <rect key={i} x={3.5 + i * 4.25} y="5.5" width="4.25" height="13"
            fill={BLU} fillOpacity={0.55 - i * 0.15} stroke="none" />))}</g>);
    case "insert.dataextract": case "insert.datalink": case "insert.updatefields":
    case "insert.field": case "anno.table.extract": case "anno.table.link":
      return g(<g {...s}><rect x="3.5" y="4.5" width="17" height="15" stroke={LN}
        strokeWidth={1.3} />
        <path d="M3.5 9h17M9.5 9v10.5M15 9v10.5" stroke={LN} strokeWidth={1.2} />
        <path d="M3.5 4.5h17v4.5h-17z" fill={BLU} fillOpacity={0.4} stroke="none" /></g>);

    // ── Parametric ──────────────────────────────────────────────────────────────────────
    case "par.autoconstrain": case "par.coincident": case "par.collinear":
    case "par.concentric": case "par.parallel": case "par.perpendicular": case "par.tangent":
    case "par.horizontal": case "par.vertical": case "par.equal": case "par.showall":
    case "par.hideall": case "par.geomgroup": case "par.paramgr": case "par.delcon":
    case "par.dimconstraint":
      return g(<g {...s}><path d="M4 17L20 7" stroke={LN} strokeWidth={1.5} />
        <circle cx="4" cy="17" r="2.4" fill={MAG} stroke="none" />
        <circle cx="20" cy="7" r="2.4" fill={MAG} stroke="none" />
        {id === "par.delcon"
          ? <path d="M8 6l8 8M16 6l-8 8" stroke={RED} strokeWidth={1.6} /> : null}</g>);

    // ── PDVN's own ──────────────────────────────────────────────────────────────────────
    case "pdvn.wall":
      return g(<g {...s}><path d="M3 8.5h18v7H3z" fill={PAPER} stroke={LN} strokeWidth={1.5} />
        <path d="M3 8.5h18M3 15.5h18" stroke={BLU} strokeWidth={1.8} /></g>);
    case "pdvn.door":
      return g(<g {...s}><path d="M4 5h3v14H4zM17 5h3v14h-3z" fill={PAPER}
        stroke={LN} strokeWidth={1.3} />
        <path d="M7 19V6l7 2v13z" fill={BLU} fillOpacity={0.3} stroke={BLU} strokeWidth={1.4} />
        <path d="M14 21a10 10 0 00-7-9" stroke={LN_D} strokeWidth={1.1}
          strokeDasharray="2.2 2" /></g>);
    case "pdvn.window":
      return g(<g {...s}><path d="M2.5 9h19v6h-19z" fill={PAPER} stroke={LN} strokeWidth={1.4} />
        <path d="M2.5 12h19" stroke={BLU} strokeWidth={1.6} /></g>);
    case "pdvn.divider": case "st.grid":
      return g(<g stroke={LN} strokeWidth={1.2} {...s}>
        <rect x="3.5" y="3.5" width="17" height="17" />
        <path d="M9.2 3.5v17M14.8 3.5v17M3.5 9.2h17M3.5 14.8h17" /></g>);
    case "pdvn.section": case "pdvn.detail": case "exp.qqui":
      return g(<g {...s}><path d="M3 5v14" stroke={LN} strokeWidth={1.3} />
        <path d="M3 8.5h13M3 15.5h13" stroke={YEL} strokeWidth={1.6} />
        <path d="M16 8.5l4-2.5v13l-4-2.5z" fill={YEL} fillOpacity={0.3} stroke={YEL}
          strokeWidth={1.3} /></g>);

    // ── the rest, and everything unnamed ────────────────────────────────────────────────
    // ⚠ Deliberately a NEUTRAL PLACEHOLDER, not a random glyph. AutoCAD has ~1 200 commands and
    // this shell names ~300 of them; a wrong-but-confident icon is worse than an obviously
    // generic one, because the eye learns it and then reaches for it.
    // ── the status bar ──────────────────────────────────────────────────────────────────
    //
    // ⚠⚠ **A ROW OF TWENTY-TWO IDENTICAL SQUARES IS NOT A STATUS BAR.** Five of these had art
    // and seventeen fell through to `default`, so the row a drafter glances at a hundred times
    // an hour to answer "is ortho on?" showed the same grey box for ortho, polar, snap, dynamic
    // input and the rest. Made worse the day the toggles started WORKING: a lit placeholder
    // tells you something is on without telling you what.
    //
    // ⚠ These read at 15 px, not 24 — the status bar is the smallest thing in the app. So they
    // are built from two or three strokes each, and the distinguishing mark is the SILHOUETTE
    // (a right angle for ortho, a fan for polar, a lattice for snap), never fine detail.
    case "st.snapmode":
      return g(<g {...s} strokeWidth={1.4}>
        <path d="M4 9h16M4 15h16M9 4v16M15 4v16" stroke={LN_D} />
        <circle cx="9" cy="15" r="2.4" fill={BLU} stroke="none" /></g>);
    case "st.ortho":
      return g(<g {...s} strokeWidth={1.7}>
        <path d="M5 19V6M5 19h13" stroke={BLU} />
        <path d="M5 12.5h5.5v6.5" stroke={LN_D} strokeWidth={1.2} /></g>);
    case "st.polar":
      return g(<g {...s} strokeWidth={1.5}>
        <path d="M5 19h13" stroke={LN_D} strokeWidth={1.2} />
        <path d="M5 19L18 6" stroke={BLU} strokeWidth={1.7} />
        <path d="M5 19a9 9 0 0 1 3.2-6.9" stroke={LN_D} strokeWidth={1.1} /></g>);
    case "st.otrack":
      return g(<g {...s} strokeWidth={1.4}>
        <path d="M4 16h16" stroke={LN_D} strokeDasharray="2.5 2" />
        <path d="M14 4v16" stroke={LN_D} strokeDasharray="2.5 2" />
        <rect x="11.5" y="13.5" width="5" height="5" stroke={BLU} strokeWidth={1.5} /></g>);
    case "st.dynin":
      return g(<g {...s} strokeWidth={1.4}>
        <path d="M3 6.5l4 4-4 4" stroke={BLU} strokeWidth={1.7} />
        <rect x="9" y="7.5" width="12" height="6" rx="1" stroke={LN_D} />
        <path d="M11.5 10.5h5" stroke={LN_D} strokeWidth={1.1} /></g>);
    case "st.infer":
      return g(<g {...s} strokeWidth={1.4}>
        <path d="M4 17l7-9 9 5" stroke={LN} />
        <circle cx="11" cy="8" r="2.2" fill={MAG} stroke="none" />
        <circle cx="20" cy="13" r="2.2" stroke={MAG} strokeWidth={1.4} /></g>);
    case "st.iso":
      return g(<g {...s} strokeWidth={1.4}>
        <path d="M12 4v8M12 12l7 4M12 12l-7 4" stroke={BLU} strokeWidth={1.6} />
        <path d="M4 8.5l8-4.5 8 4.5v7l-8 4.5-8-4.5z" stroke={LN_D} strokeWidth={1.1} /></g>);
    case "st.lwt":
      return g(<g {...s}>
        <path d="M4 7.5h16" stroke={LN} strokeWidth={1} />
        <path d="M4 12h16" stroke={LN} strokeWidth={2.2} />
        <path d="M4 16.5h16" stroke={BLU} strokeWidth={3.6} /></g>);
    case "st.cycling":
      return g(<g {...s} strokeWidth={1.4}>
        <rect x="4" y="7" width="10" height="10" stroke={LN_D} />
        <rect x="8" y="4" width="10" height="10" stroke={LN_D} />
        <path d="M15 17.5a4 4 0 1 0 4-4" stroke={BLU} strokeWidth={1.5} />
        <path d="M19 10.5v3.2h-3.2" stroke={BLU} strokeWidth={1.4} /></g>);
    case "st.qp":
      return g(<g {...s} strokeWidth={1.4}>
        <rect x="5" y="4.5" width="14" height="15" rx="1.2" stroke={LN_D} />
        <path d="M8 9h8M8 12.5h8M8 16h4.5" stroke={LN} strokeWidth={1.2} />
        <path d="M16.5 4.5l-2 6h3l-2 5" stroke={YEL} strokeWidth={1.5} /></g>);
    case "st.units":
      return g(<g {...s} strokeWidth={1.4}>
        <path d="M3.5 9.5h17v5h-17z" stroke={LN_D} />
        <path d="M7 9.5v3M10.5 9.5v5M14 9.5v3M17.5 9.5v5" stroke={LN} strokeWidth={1.1} /></g>);
    case "st.annovis": case "st.annomon": case "st.autoscale": case "st.osnap":
      return g(<g {...s} strokeWidth={1.4}>
        <path d="M4 18L12 5l8 13z" stroke={LN_D} />
        <circle cx="12" cy="5" r="2" fill={BLU} stroke="none" />
        <circle cx="4" cy="18" r="2" fill={BLU} stroke="none" />
        <circle cx="20" cy="18" r="2" fill={BLU} stroke="none" /></g>);
    case "st.gfx":
      return g(<g {...s} strokeWidth={1.4}>
        <rect x="3.5" y="6" width="17" height="12" rx="1.2" stroke={LN_D} />
        <path d="M6.5 14l2.5-4 2.5 3 2.5-5 3.5 6" stroke={GRN} strokeWidth={1.5} /></g>);
    case "st.workspace":
      return g(<g {...s} strokeWidth={1.4}>
        <rect x="3.5" y="5" width="17" height="14" rx="1.2" stroke={LN_D} />
        <path d="M3.5 9.5h17" stroke={LN_D} />
        <path d="M6.5 7.2h2M10 7.2h2" stroke={BLU} strokeWidth={1.4} /></g>);

    // ── the 3D workspaces: Solid, Surface, Mesh, Visualize ──────────────────────────────
    //
    // ⚠⚠ **A TAB OF IDENTICAL PLACEHOLDER SQUARES IS WORSE THAN NO TAB.** The three 3D tabs
    // shipped with sixty-odd commands and no art, so `default` drew every one of them as the
    // same grey rounded box — and a panel where Box, Cylinder and Cone are visually identical
    // cannot be read at all, which is the exact failure this file's own header describes. The
    // grammar is unchanged: pale line for the geometry, blue for what the command adds, red for
    // what it removes, a dashed ghost for the "before".
    //
    // ⚠ Commands SHARE a glyph where AutoCAD's own do — the seven mesh primitives are one
    // flyout off one button in AutoCAD too, and drawing seven near-identical wireframes would
    // be effort spent making a distinction the product does not make.
    case "sol.box": case "mesh.box": case "mesh.wedge":
      return g(<g {...s} strokeWidth={W}>
        <path d="M4 8.5l8-4 8 4v7l-8 4-8-4z" fill={PAPER} stroke={LN} />
        <path d="M4 8.5l8 4 8-4M12 12.5v7" stroke={LN_D} strokeWidth={1.2} /></g>);
    case "sol.cylinder": case "mesh.cylinder":
      return g(<g {...s} strokeWidth={W}>
        <path d="M6.5 6.5h11v11H6.5z" fill={PAPER} stroke="none" />
        <ellipse cx="12" cy="6.5" rx="5.5" ry="2.4" fill={PAPER} stroke={LN} />
        <path d="M6.5 6.5v11M17.5 6.5v11" stroke={LN} />
        <path d="M6.5 17.5a5.5 2.4 0 0 0 11 0" stroke={LN} />
        <path d="M6.5 17.5a5.5 2.4 0 0 1 11 0" stroke={LN_D} strokeWidth={1.1} /></g>);
    case "sol.cone": case "mesh.cone":
      return g(<g {...s} strokeWidth={W}>
        <path d="M12 4l5.5 13.5h-11z" fill={PAPER} stroke={LN} />
        <path d="M6.5 17.5a5.5 2.4 0 0 0 11 0" stroke={LN} />
        <path d="M6.5 17.5a5.5 2.4 0 0 1 11 0" stroke={LN_D} strokeWidth={1.1} /></g>);
    case "sol.sphere": case "mesh.sphere":
      return g(<g {...s} strokeWidth={W}>
        <circle cx="12" cy="12" r="8" fill={PAPER} stroke={LN} />
        <ellipse cx="12" cy="12" rx="8" ry="3.2" stroke={LN_D} strokeWidth={1.2} />
        <ellipse cx="12" cy="12" rx="3.2" ry="8" stroke={LN_D} strokeWidth={1.2} /></g>);
    case "sol.pyramid": case "mesh.pyramid":
      return g(<g {...s} strokeWidth={W}>
        <path d="M12 3.5l7.5 11.5-7.5 5-7.5-5z" fill={PAPER} stroke={LN} />
        <path d="M12 3.5v16.5M4.5 15l7.5 2 7.5-2" stroke={LN_D} strokeWidth={1.2} /></g>);
    case "sol.wedge":
      return g(<g {...s} strokeWidth={W}>
        <path d="M4 18.5V9l9-4.5v9z" fill={PAPER} stroke={LN} />
        <path d="M4 18.5h9l7-4.5-7-1" stroke={LN} /></g>);
    case "sol.torus": case "mesh.torus":
      return g(<g {...s} strokeWidth={W}>
        <ellipse cx="12" cy="12" rx="8.5" ry="5" fill={PAPER} stroke={LN} />
        <ellipse cx="12" cy="12" rx="3.2" ry="1.7" stroke={LN_D} strokeWidth={1.2} /></g>);
    case "sol.polysolid":
      return g(<g {...s} strokeWidth={W}>
        <path d="M4 17V9l6-3 6 3v8" stroke={LN} />
        <path d="M6.5 18.5V10l5.5-2.7 5.5 2.7v8.5" stroke={BLU} strokeWidth={1.3} /></g>);
    case "sol.extrude": case "surf.extrude": case "sol.presspull":
      return g(<g {...s} strokeWidth={W}>
        <path d="M5 18.5h9l5-3h-9z" fill={PAPER} stroke={LN} />
        <path d="M9.5 13V5M9.5 4l-2.5 3M9.5 4l2.5 3" stroke={BLU} strokeWidth={1.6} /></g>);
    case "sol.revolve": case "surf.revolve":
      return g(<g {...s} strokeWidth={W}>
        <path d="M5 3.5v17" stroke={RED} strokeWidth={1.4} strokeDasharray="3 2" />
        <path d="M10 6.5h6v11h-6z" fill={PAPER} stroke={LN} />
        <path d="M8 5.5a8 8 0 0 1 0 13" stroke={BLU} strokeWidth={1.4} /></g>);
    case "sol.sweep": case "surf.sweep":
      return g(<g {...s} strokeWidth={W}>
        <path d="M4 18c5-1 5-11 10-12" stroke={BLU} strokeWidth={1.5} />
        <path d="M12 3.5l4 1.5-3.5 2.5z" fill={PAPER} stroke={LN} />
        <rect x="2.5" y="15.5" width="5" height="5" rx="0.6" fill={PAPER} stroke={LN} /></g>);
    case "sol.loft": case "surf.loft":
      return g(<g {...s} strokeWidth={W}>
        <path d="M4.5 19.5h10l3-2h-10z" fill={PAPER} stroke={LN} />
        <path d="M8 7.5h7l2-1.5h-7z" fill={PAPER} stroke={LN} />
        <path d="M4.5 19.5L8 7.5M14.5 19.5L15 7.5" stroke={BLU} strokeWidth={1.3} /></g>);
    case "sol.interfere":
      return g(<g {...s} strokeWidth={W}>
        <rect x="3.5" y="7.5" width="10" height="10" rx="1" stroke={LN} />
        <rect x="10.5" y="7.5" width="10" height="10" rx="1" stroke={LN} />
        <rect x="10.5" y="7.5" width="3" height="10" fill={RED} fillOpacity={0.5}
              stroke={RED} strokeWidth={1.2} /></g>);
    case "sol.slice":
      return g(<g {...s} strokeWidth={W}>
        <path d="M5 8.5l7-3.5 7 3.5v7l-7 3.5-7-3.5z" fill={PAPER} stroke={LN} />
        <path d="M3 16.5L21 6" stroke={RED} strokeWidth={1.5} /></g>);
    case "sol.thicken": case "surf.offset":
      return g(<g {...s} strokeWidth={W}>
        <path d="M4 14c4-5 12-5 16 0" stroke={LN} />
        <path d="M4 18c4-5 12-5 16 0" stroke={BLU} strokeWidth={1.4} strokeDasharray="3 2" /></g>);
    case "sol.shell":
      return g(<g {...s} strokeWidth={W}>
        <path d="M5 8.5l7-3.5 7 3.5v7l-7 3.5-7-3.5z" fill="none" stroke={LN} />
        <path d="M8 10l4-2 4 2v4l-4 2-4-2z" stroke={BLU} strokeWidth={1.3} /></g>);
    case "sol.imprint": case "sol.extractedges": case "sol.offsetedge":
      return g(<g {...s} strokeWidth={W}>
        <path d="M5 8.5l7-3.5 7 3.5v7l-7 3.5-7-3.5z" fill={PAPER} stroke={LN} />
        <path d="M5 8.5l7 3.5 7-3.5" stroke={BLU} strokeWidth={1.5} /></g>);
    case "sol.sectionblock": case "sol.livesection":
      return g(<g {...s} strokeWidth={W}>
        <path d="M5 8.5l7-3.5 7 3.5v7l-7 3.5-7-3.5z" fill={PAPER} stroke={LN} />
        <path d="M12 5v14" stroke={GRN} strokeWidth={1.6} />
        <path d="M12 12l3-2M12 12l3 2" stroke={GRN} strokeWidth={1.3} /></g>);
    case "surf.network": case "surf.planar": case "surf.patch":
      return g(<g {...s} strokeWidth={W}>
        <path d="M4 15.5l8-4 8 4-8 4z" fill={PAPER} stroke={LN} />
        <path d="M4 15.5c2-6 14-6 16 0" stroke={BLU} strokeWidth={1.3} /></g>);
    case "surf.blend": case "surf.fillet":
      return g(<g {...s} strokeWidth={W}>
        <path d="M3.5 6h6M3.5 6v6" stroke={LN} />
        <path d="M20.5 18h-6M20.5 18v-6" stroke={LN} />
        <path d="M9.5 6c5 0 5 12 10 12" stroke={BLU} strokeWidth={1.5} /></g>);
    case "surf.trim": case "surf.untrim": case "surf.extend":
      return g(<g {...s} strokeWidth={W}>
        <path d="M4 15.5l8-4 8 4-8 4z" fill={PAPER} stroke={LN} />
        <path d="M12 3.5v9" stroke={RED} strokeWidth={1.5} strokeDasharray="3 2" /></g>);
    case "surf.sculpt": case "surf.project": case "mesh.tosolid":
    case "mesh.tosurface": case "mesh.smoothobj":
      return g(<g {...s} strokeWidth={W}>
        <path d="M4 14.5l8-4 8 4-8 4z" stroke={LN_D} strokeWidth={1.2} />
        <path d="M5 8.5l7-3.5 7 3.5v3l-7 3.5-7-3.5z" fill={PAPER} stroke={BLU}
              strokeWidth={1.3} /></g>);
    case "surf.zebra": case "surf.curvature": case "surf.draft":
      return g(<g {...s} strokeWidth={W}>
        <circle cx="12" cy="12" r="8" fill={PAPER} stroke={LN} />
        <path d="M6 8.5c4 2 8 2 12 0M5 12c4.5 2.2 9.5 2.2 14 0M6 15.5c4 2 8 2 12 0"
              stroke={GRN} strokeWidth={1.3} /></g>);
    case "mesh.revsurf": case "mesh.edgesurf": case "mesh.rulesurf":
      return g(<g {...s} strokeWidth={W}>
        <path d="M4 15.5l8-4 8 4-8 4z" fill={PAPER} stroke={LN} />
        <path d="M8 13.5l8 4M16 13.5l-8 4" stroke={LN_D} strokeWidth={1.1} /></g>);
    case "mesh.smoothmore": case "mesh.smoothless": case "mesh.refine":
      return g(<g {...s} strokeWidth={W}>
        <Ghost d="M4 17l8-8 8 8" />
        <path d="M4 17c4-9 12-9 16 0" stroke={BLU} strokeWidth={1.5} /></g>);
    case "mesh.crease": case "mesh.uncrease": case "mesh.split":
      return g(<g {...s} strokeWidth={W}>
        <path d="M4 17l8-8 8 8z" fill={PAPER} stroke={LN} />
        <path d="M12 9v8" stroke={RED} strokeWidth={1.5} /></g>);
    case "vis.pointlight": case "vis.spotlight": case "vis.distant":
      return g(<g {...s} strokeWidth={W}>
        <circle cx="12" cy="9" r="4" fill={YEL} fillOpacity={0.5} stroke={YEL} />
        <path d="M12 2v2.5M12 13.5V16M4.5 9H7M17 9h2.5M6.7 3.7l1.8 1.8M15.5 5.5l1.8-1.8"
              stroke={YEL} strokeWidth={1.3} />
        <path d="M7 20.5h10" stroke={LN} /></g>);
    case "vis.sun": case "vis.geoloc":
      return g(<g {...s} strokeWidth={W}>
        <circle cx="12" cy="13" r="4.5" fill={YEL} fillOpacity={0.45} stroke={YEL} />
        <path d="M3 20.5h18" stroke={LN} />
        <path d="M12 3v3M4.5 6.5l2 2M19.5 6.5l-2 2" stroke={YEL} strokeWidth={1.3} /></g>);
    case "vis.matbrowser": case "vis.mateditor": case "vis.matattach":
      return g(<g {...s} strokeWidth={W}>
        <circle cx="9.5" cy="11" r="5.5" fill={PAPER} stroke={LN} />
        <circle cx="15" cy="14" r="5.5" fill={BLU} fillOpacity={0.35} stroke={BLU}
                strokeWidth={1.3} /></g>);
    case "vis.render": case "vis.rendercrop": case "vis.renderwin":
    case "vis.renderenv": case "vis.renderpresets":
      return g(<g {...s} strokeWidth={W}>
        <rect x="3.5" y="5.5" width="17" height="13" rx="1.2" fill={PAPER} stroke={LN} />
        <path d="M3.5 15l5-4.5 4 3.5 3.5-3 4.5 4" stroke={BLU} strokeWidth={1.4} />
        <circle cx="8" cy="9.5" r="1.6" fill={YEL} stroke="none" /></g>);
    case "view.camera":
      return g(<g {...s} strokeWidth={W}>
        <path d="M3.5 8.5h10v8h-10z" fill={PAPER} stroke={LN} />
        <path d="M13.5 12l6-3.5v11l-6-3.5z" fill={PAPER} stroke={LN} />
        <circle cx="8.5" cy="12.5" r="2.2" stroke={BLU} strokeWidth={1.3} /></g>);

    default:
      return g(<g {...s}>
        <rect x="4.5" y="4.5" width="15" height="15" rx="1.6" stroke={LN_D} strokeWidth={1.3} />
        <path d="M8.5 12h7" stroke={LN_D} strokeWidth={1.3} /></g>);
  }
}

export default Icon;
